import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Calendar, Clock } from "lucide-react";

export const ParentTrialSettingsCard = ({ trialSettings, onToggle, onNumberChange, disabled }) => {
  // Compute an approximate end date from today
  const endDate = new Date();
  endDate.setDate(endDate.getDate() + Number(trialSettings.defaultTrialDays || 7));
  const endDateLabel = endDate.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });

  return (
    <Card className="overflow-hidden border-2 border-emerald-100 bg-white shadow-xl" data-testid="parent-trial-settings-card">
      <CardHeader className="bg-gradient-to-r from-emerald-600 to-teal-500 text-center py-6">
        <CardTitle className="text-2xl font-bold text-white tracking-tight">
          Parent Trial Settings
        </CardTitle>
        <CardDescription className="text-emerald-50 mt-1">
          Set how long parents get to try the portal before a payment is required
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-5 p-6">

        <div className={`flex items-center justify-between gap-4 rounded-xl border px-4 py-3.5 transition ${trialSettings.trialOffersEnabled ? "border-emerald-200 bg-emerald-50/50" : "border-slate-200 bg-slate-50"}`}>
          <div>
            <p className="font-semibold text-slate-900" data-testid="parent-trial-offers-enabled-title">Enable trial offers</p>
            <p className="text-sm text-slate-500" data-testid="parent-trial-offers-enabled-description">Allow parents to access the portal for a set number of days before requiring payment.</p>
          </div>
          <Switch
            checked={trialSettings.trialOffersEnabled}
            onCheckedChange={(value) => onToggle("trialOffersEnabled", value)}
            disabled={disabled}
            data-testid="parent-trial-offers-enabled-switch"
          />
        </div>

        <div className="rounded-xl border border-slate-200 bg-white p-5 space-y-4 text-center">
          <div className="flex items-center justify-center gap-2">
            <Clock className="h-4 w-4 text-emerald-600" />
            <label className="text-sm font-semibold text-slate-900" htmlFor="parent-default-trial-days">Trial length (days)</label>
          </div>

          <div className="flex justify-center">
            <Input
              id="parent-default-trial-days"
              type="number"
              min="1"
              max="90"
              disabled={disabled || !trialSettings.trialOffersEnabled}
              value={trialSettings.defaultTrialDays}
              onChange={(e) => onNumberChange("defaultTrialDays", e.target.value)}
              className="w-28 text-center text-2xl font-bold border-2 border-emerald-600 focus:border-emerald-700 rounded-xl"
              data-testid="parent-default-trial-days-input"
            />
          </div>

          <div className="flex items-center justify-center gap-2 rounded-xl border border-slate-300 bg-slate-50 px-4 py-3" data-testid="parent-trial-calendar-preview">
            <Calendar className="h-5 w-5 text-emerald-600 flex-shrink-0" />
            <p className="text-sm font-medium text-slate-700">
              Trial ends on <span className="font-bold text-emerald-700">{endDateLabel}</span>
            </p>
          </div>

          <p className="text-xs text-slate-500" data-testid="parent-default-trial-days-hint">
            After <strong>{trialSettings.defaultTrialDays} days</strong>, parents see an upgrade screen with your Zelle or Square payment options. Once paid, their status changes to <strong>Active</strong>.
          </p>
        </div>

      </CardContent>
    </Card>
  );
};
